/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import { PaginatedResult, StoreQueryParams } from '../../../../ts';
import { Assistant, Environment, Multilingual } from '../../../../ts/assistant';
import { Header } from '../../../../ts/header';
import { PublishStatus, Release } from '../../../../ts/release';
import { ReactivateSession } from '../../../../ts/session';
import { endpoints } from '../../util/config';
import { formatAssistantToEnvironment } from '../environments';

const endpoint = `${endpoints.store}/v2/assistants`;

const auditParams: StoreQueryParams = {
  include_audit: true,
  verbose: true
};

const formatReleaseTimestamps = (release: Release): Release => {
  return {
    updated: release?.updated || release?.created,
    ...release
  };
};
export interface PaginatedReleases extends PaginatedResult {
  releases: Release[];
}

/**
 * List releases for an assistant.
 */
export const listReleases = async (
  assistantId: string,
  reactivateSession?: ReactivateSession
): Promise<PaginatedReleases> => {
  const response = await axios.get<PaginatedReleases>(
    urlJoin(endpoint, assistantId, 'releases', {
      query: {
        reactivateSession
      }
    }),
    {
      params: {
        ...auditParams
      }
    }
  );
  const releases = response.data.releases.map(formatReleaseTimestamps);
  return { releases };
};

/**
 * Delete a release
 *
 */
export const deleteRelease = async (
  assistantId: string,
  release: string
): Promise<{}> => {
  const response = await axios.delete(
    `${urlJoin(endpoint, assistantId)}/releases/${release}`,
    {
      params: {
        ...auditParams
      }
    }
  );
  return response.data;
};

/**
 * Creates a release
 */
export const createRelease = async (
  assistantId: string,
  description: string
): Promise<Release> => {
  const response = await axios.post<Release>(
    `${urlJoin(endpoint, assistantId)}/releases`,
    {
      description: description || '_'
    }
  );
  return formatReleaseTimestamps(response.data);
};

/**
 * Deploys a release
 */
export const deployRelease = async (
  assistantId: string,
  release: string,
  environmentId: string,
  environmentName: string
): Promise<Environment> => {
  // FIXME: use `environment_id` in publish endpoint
  const response = await axios.post<Assistant>(
    `${urlJoin(endpoint, assistantId)}/releases/${release}/deploy`,
    {
      environment: environmentName,
      environment_id: environmentId
    }
  );

  // FIXME: Remove after multi-environment support is added to all environments
  return formatAssistantToEnvironment(response.data);
};

/**
 * Deploy a release
 */
export const publishRelease = async (
  assistantId: string,
  description: string,
  environment_ids: string[]
): Promise<Pick<Release, 'release' | 'status'>> => {
  const response = await axios.post(
    `${urlJoin(endpoints.store, 'assistants', assistantId, 'publish')}`,
    {
      description,
      environment_ids
    }
  );
  return response.data;
};

/**
 * Get the status of a particular release created by the client (e.g. because we had to save draft content
 * during a revert version operation).
 */
export const getRelease = async (
  assistantId: string,
  release: string
): Promise<Release> => {
  const response = await axios.get<Release>(
    `${urlJoin(endpoint, assistantId)}/releases/${release}`,
    {
      params: {
        ...auditParams
      }
    }
  );
  return formatReleaseTimestamps(response.data);
};

/**
 * Get the end-to-end publish status for a given assistant.
 * This will take into account both the creation of the release as well as its deployment.
 */
export const getPublishStatus = async (
  assistantId: string
): Promise<PublishStatus> => {
  const response = await axios.get(
    urlJoin(endpoints.store, 'assistants', assistantId, 'publish', {
      query: {
        reactivateSession: ReactivateSession.NEVER
      }
    }),
    {
      params: {
        ...auditParams
      }
    }
  );
  return response.data;
};

/**
 * Create a multilingual export
 */
export const createMultilingualExport = async (
  assistantId: string,
  releaseId: string
): Promise<Multilingual> => {
  const response = await axios.post(
    `${urlJoin(
      endpoint,
      assistantId
    )}/releases/${releaseId}/multi_lingual/export`
  );
  return response.data;
};

/**
 * Get the response of a particular multilingual export created by the client
 * A status will only be returned when the system is still completing the job or if said job has failed due to some reason.
 * When the underlying multilingual export job has completed successfully, no status will be returned: only a ZIP file artifact will be returned.
 *
 * In other words, there are only 2 possible response types from this GET API:
 * 1) A ZIP file which means the file export has completed successfully, and the response header content-type will always contain "application/zip";
 * 2) A JSON object which means the file export has a status equal to either "Processing" or "Failed", and the response header content-type will always contain "application/json"
 *
 */
export const getMultilingualExport = async (
  assistantId: string,
  releaseId: string
): Promise<any> => {
  const response = await axios.get(
    `${urlJoin(
      endpoint,
      assistantId
    )}/releases/${releaseId}/multi_lingual/export`,
    {
      params: {
        ...auditParams
      },
      responseType: 'arraybuffer',
      transformResponse: (responseBody, headers) => {
        if (headers[Header.CONTENT_TYPE].includes('application/zip')) {
          return responseBody;
        } else if (headers[Header.CONTENT_TYPE].includes('application/json')) {
          return JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(responseBody))
          );
        } else {
          console.error('Unexpected content type');
        }
      }
    }
  );

  return response.data;
};

/**
 * Create an assistant release export
 */
export const createAssistantReleaseExport = async (
  assistantId: string,
  releaseId: string
): Promise<Multilingual> => {
  const response = await axios.post(
    `${urlJoin(endpoint, assistantId)}/releases/${releaseId}/export`
  );
  return response.data;
};

/**
 * Get the response of a particular assistant export created by the client
 * A status will only be returned when the system is still completing the job or if said job has failed due to some reason.
 * When the underlying assistant release export job has completed successfully, no status will be returned: only a ZIP file artifact will be returned.
 *
 * In other words, there are only 2 possible response types from this GET API:
 * 1) A ZIP file which means the file export has completed successfully, and the response header content-type will always contain "application/zip";
 * 2) A JSON object which means the file export has a status equal to either "Processing" or "Failed", and the response header content-type will always contain "application/json"
 *
 */
export const getAssistantReleaseExport = async (
  assistantId: string,
  releaseId: string
): Promise<any> => {
  const response = await axios.get(
    `${urlJoin(endpoint, assistantId)}/releases/${releaseId}/export`,
    {
      params: {
        ...auditParams
      },
      responseType: 'arraybuffer',
      transformResponse: (responseBody, headers) => {
        if (headers[Header.CONTENT_TYPE].includes('application/zip')) {
          return responseBody;
        } else if (headers[Header.CONTENT_TYPE].includes('application/json')) {
          return JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(responseBody))
          );
        } else {
          console.error(
            'Unexpected content type: ' + `${headers[Header.CONTENT_TYPE]}`
          );
        }
      }
    }
  );

  return response.data;
};
